import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle, Scale, UserX, Shield } from 'lucide-react';
export function TermsOfServicePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1915">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1916">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1917">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1918">
            <span className="w-8 h-px bg-teal" data-id="element-1919"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1920">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1921"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1922">
            Terms of Service
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1923">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1924">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1925">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1926">
            These Terms of Service ("Terms") govern your access to and use of
            SyNeural's platform, services, and website (collectively, the
            "Services"). By accessing or using our Services, you agree to be
            bound by these Terms.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1927">
            If you do not agree to these Terms, you may not access or use our
            Services. If you are using our Services on behalf of an
            organization, you represent that you have the authority to bind that
            organization to these Terms.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1928">
        {/* Acceptance of Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1929">
          <div className="flex items-center gap-4 mb-6" data-id="element-1930">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1931">
              <CheckCircle size={24} data-id="element-1932" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1933">
              Acceptance of Terms
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1934">
            By creating an account, accessing our platform, or using any of our
            Services, you acknowledge that you have read, understood, and agree
            to be bound by these Terms, as well as our Privacy Policy. These
            Terms constitute a legally binding agreement between you and
            SyNeural.
          </p>
        </motion.div>

        {/* Eligibility */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1935">
          <div className="flex items-center gap-4 mb-6" data-id="element-1936">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1937">
              <UserX size={24} data-id="element-1938" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1939">
              Eligibility
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1940">
            To use our Services, you must:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1941">
            <li data-id="element-1942">
              Be at least 13 years of age (or the age of majority in your
              jurisdiction)
            </li>
            <li data-id="element-1943">Have the legal capacity to enter into a binding agreement</li>
            <li data-id="element-1944">
              Not be prohibited from using our Services under applicable law
            </li>
            <li data-id="element-1945">Provide accurate and complete registration information</li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1946">
            For users under 18, access through educational institutions or with
            parental consent may be required.
          </p>
        </motion.div>

        {/* Account Registration and Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1947">
          <div className="flex items-center gap-4 mb-6" data-id="element-1948">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1949">
              <Shield size={24} data-id="element-1950" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1951">
              Account Registration and Security
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1952">
            <p data-id="element-1953">When you create an account, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1954">
              <li data-id="element-1955">Provide accurate, current, and complete information</li>
              <li data-id="element-1956">Maintain and update your information to keep it accurate</li>
              <li data-id="element-1957">
                Maintain the security and confidentiality of your account
                credentials
              </li>
              <li data-id="element-1958">
                Notify us immediately of any unauthorized access or security
                breach
              </li>
              <li data-id="element-1959">
                Accept responsibility for all activities that occur under your
                account
              </li>
            </ul>
            <p data-id="element-1960">
              You may not share your account credentials, create multiple
              accounts, or allow others to use your account. SyNeural reserves
              the right to suspend or terminate accounts that violate these
              requirements.
            </p>
          </div>
        </motion.div>

        {/* Use of Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1961">
          <div className="flex items-center gap-4 mb-6" data-id="element-1962">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1963">
              <FileText size={24} data-id="element-1964" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1965">
              Permitted Use
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1966">
            You may use our Services for lawful purposes in accordance with
            these Terms. You agree not to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1967">
            <li data-id="element-1968">
              Violate any applicable laws, regulations, or third-party rights
            </li>
            <li data-id="element-1969">
              Use the Services for any fraudulent, harmful, or malicious purpose
            </li>
            <li data-id="element-1970">
              Attempt to gain unauthorized access to our systems or other users'
              accounts
            </li>
            <li data-id="element-1971">
              Interfere with or disrupt the integrity or performance of the
              Services
            </li>
            <li data-id="element-1972">
              Reverse engineer, decompile, or attempt to extract source code
            </li>
            <li data-id="element-1973">
              Use automated tools (bots, scrapers) without our written
              permission
            </li>
            <li data-id="element-1974">Misrepresent your identity or affiliation</li>
            <li data-id="element-1975">
              Share, sell, or redistribute assessment content or results without
              authorization
            </li>
            <li data-id="element-1976">Use the Services to harass, abuse, or harm others</li>
          </ul>
        </motion.div>

        {/* Intellectual Property */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1977">
          <div className="flex items-center gap-4 mb-6" data-id="element-1978">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1979">
              <Scale size={24} data-id="element-1980" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1981">
              Intellectual Property Rights
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1982">
            <p data-id="element-1983">
              All content, features, functionality, and materials available
              through our Services, including but not limited to text, graphics,
              logos, assessment frameworks, algorithms, software, and data
              compilations, are owned by SyNeural or its licensors and are
              protected by copyright, trademark, patent, and other intellectual
              property laws.
            </p>
            <p data-id="element-1984">
              We grant you a limited, non-exclusive, non-transferable, revocable
              license to access and use our Services for your personal or
              internal organizational use. This license does not include any
              right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1985">
              <li data-id="element-1986">
                Reproduce, distribute, or create derivative works from our
                content
              </li>
              <li data-id="element-1987">
                Use our trademarks, logos, or branding without written
                permission
              </li>
              <li data-id="element-1988">
                Remove or alter any copyright, trademark, or proprietary notices
              </li>
              <li data-id="element-1989">Commercially exploit our Services or content</li>
            </ul>
          </div>
        </motion.div>

        {/* User Content and Data */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1990">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1991">
            User Content and Data
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1992">
            <p data-id="element-1993">
              You retain ownership of any content, data, or information you
              provide through our Services ("User Content"). By providing User
              Content, you grant SyNeural a worldwide, non-exclusive,
              royalty-free license to use, process, analyze, and store such
              content to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1994">
              <li data-id="element-1995">Provide and improve our Services</li>
              <li data-id="element-1996">Generate assessment results and recommendations</li>
              <li data-id="element-1997">
                Conduct research and platform development (using anonymized
                data)
              </li>
              <li data-id="element-1998">Comply with legal obligations</li>
            </ul>
            <p data-id="element-1999">
              You represent and warrant that you have all necessary rights to
              provide User Content and that such content does not violate any
              third-party rights or applicable laws.
            </p>
          </div>
        </motion.div>

        {/* Payment and Subscriptions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2000">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-2001">
            Payment and Subscriptions
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-2002">
            <p data-id="element-2003">
              Certain features of our Services may require payment. If you
              purchase a subscription or paid service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-2004">
              <li data-id="element-2005">
                You agree to pay all applicable fees as described at the time of
                purchase
              </li>
              <li data-id="element-2006">
                Fees are non-refundable except as required by law or as
                specified in our refund policy
              </li>
              <li data-id="element-2007">
                Subscriptions automatically renew unless canceled before the
                renewal date
              </li>
              <li data-id="element-2008">
                We reserve the right to change pricing with advance notice
              </li>
              <li data-id="element-2009">
                You are responsible for all taxes associated with your purchase
              </li>
            </ul>
            <p data-id="element-2010">
              Failure to pay fees may result in suspension or termination of
              your access to paid Services.
            </p>
          </div>
        </motion.div>

        {/* Disclaimers and Limitations */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2011">
          <div className="flex items-center gap-4 mb-6" data-id="element-2012">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-2013">
              <AlertCircle size={24} data-id="element-2014" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-2015">
              Disclaimers and Limitations of Liability
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-2016">
            <p className="font-semibold" data-id="element-2017">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            </p>
            <p data-id="element-2018">
              SyNeural disclaims all warranties, including but not limited to
              warranties of merchantability, fitness for a particular purpose,
              non-infringement, and accuracy. We do not warrant that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-2019">
              <li data-id="element-2020">The Services will be uninterrupted, secure, or error-free</li>
              <li data-id="element-2021">
                Assessment results will be accurate, complete, or suitable for
                any particular purpose
              </li>
              <li data-id="element-2022">Any defects or errors will be corrected</li>
              <li data-id="element-2023">The Services will meet your specific requirements</li>
            </ul>
            <p className="font-semibold mt-6" data-id="element-2024">LIMITATION OF LIABILITY:</p>
            <p data-id="element-2025">
              To the maximum extent permitted by law, SyNeural shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, including but not limited to loss of profits,
              data, or opportunities, arising out of or related to your use of
              the Services, even if we have been advised of the possibility of
              such damages.
            </p>
            <p data-id="element-2026">
              Our total liability for any claims related to the Services shall
              not exceed the amount you paid us in the twelve months preceding
              the claim.
            </p>
          </div>
        </motion.div>

        {/* Indemnification */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2027">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-2028">
            Indemnification
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-2029">
            You agree to indemnify, defend, and hold harmless SyNeural, its
            affiliates, officers, directors, employees, and agents from any
            claims, liabilities, damages, losses, costs, or expenses (including
            reasonable attorneys' fees) arising out of or related to: (a) your
            use of the Services; (b) your violation of these Terms; (c) your
            violation of any third-party rights; or (d) your User Content.
          </p>
        </motion.div>

        {/* Termination */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2030">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-2031">
            Termination
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-2032">
            <p data-id="element-2033">
              You may terminate your account at any time by contacting us or
              using account settings. We may suspend or terminate your access to
              the Services at any time, with or without cause or notice,
              including if:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-2034">
              <li data-id="element-2035">You violate these Terms</li>
              <li data-id="element-2036">Your use poses a security or legal risk</li>
              <li data-id="element-2037">You fail to pay applicable fees</li>
              <li data-id="element-2038">We discontinue the Services</li>
            </ul>
            <p data-id="element-2039">
              Upon termination, your right to use the Services will immediately
              cease. Provisions that by their nature should survive termination
              (including intellectual property rights, disclaimers, and
              limitations of liability) will remain in effect.
            </p>
          </div>
        </motion.div>

        {/* Governing Law and Dispute Resolution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2040">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-2041">
            Governing Law and Dispute Resolution
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-2042">
            These Terms shall be governed by and construed in accordance with
            the laws of [Jurisdiction], without regard to its conflict of law
            provisions. Any disputes arising out of or related to these Terms or
            the Services shall be resolved through binding arbitration in
            accordance with [Arbitration Rules], except that either party may
            seek injunctive relief in court. You waive any right to participate
            in class actions or class arbitrations.
          </p>
        </motion.div>

        {/* Changes to Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2043">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-2044">
            Changes to These Terms
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-2045">
            We reserve the right to modify these Terms at any time. We will
            notify you of material changes by posting the updated Terms on our
            website and updating the "Last Updated" date. Your continued use of
            the Services after such changes constitutes acceptance of the
            updated Terms. If you do not agree to the modified Terms, you must
            stop using the Services.
          </p>
        </motion.div>

        {/* Contact Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-2046">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-2047">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-2048">
            If you have questions about these Terms of Service, please contact
            us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-2049">
            <p data-id="element-2050">
              <strong data-id="element-2051">Email:</strong>{' '}
              <a href="mailto:legal@syneural.com" className="text-teal font-medium hover:underline" data-id="element-2052">
                legal@syneural.com
              </a>
            </p>
            <p data-id="element-2053">
              <strong data-id="element-2054">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-2055">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}